import { useState } from "react";
import { getWeekdayNightSlots } from "@/lib/dates";
import type { ResponseSummary, Participant } from "@/hooks/useAvailabilityData";

interface SummaryBoardProps {
  summary: ResponseSummary;
  participants: Participant[];
}

export function SummaryBoard({ summary, participants }: SummaryBoardProps) {
  const slots = getWeekdayNightSlots();
  const bestNight = summary.bestNight;
  const [expandedSlot, setExpandedSlot] = useState<string | null>(null);
  const [showPeoplePopover, setShowPeoplePopover] = useState(false);

  return (
    <div className="planner-box summary-board">
      <h2 className="planner-title">📊 Response Summary</h2>

      <div className="summary-stats">
        {bestNight && (
          <div className="stat-box best-night">
            <div className="stat-date">
              {(() => {
                const slot = slots.find((s) => s.id === bestNight.date);
                return slot ? `${slot.dayName}, ${slot.displayDate}` : bestNight.date;
              })()}
            </div>
            <div className="stat-label">Most Popular</div>
            <div className="stat-number stat-number-secondary">{bestNight.count} votes</div>
          </div>
        )}
        <div
          className="stat-box stat-box-secondary stat-box-with-popover"
          onMouseEnter={() => setShowPeoplePopover(true)}
          onMouseLeave={() => setShowPeoplePopover(false)}
        >
          <div className="stat-number">{summary.totalResponses}</div>
          <div className="stat-label">People Replied</div>
          {showPeoplePopover && participants.length > 0 && (
            <div className="stat-popover">
              <div className="popover-title">People who replied:</div>
              <ul className="popover-list">
                {participants.map((participant) => (
                  <li key={participant.email} className="popover-item">
                    {participant.name}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>

      <div className="availability-breakdown">
        <h3 className="planner-subtitle">Availability Breakdown:</h3>
        <div className="breakdown-list">
          {slots.map((slot) => {
            const count = summary.availabilityCounts[slot.id] || 0;
            const isBest = bestNight?.date === slot.id;
            const isExpanded = expandedSlot === slot.id;
            const voters = participants.filter((p) =>
              Array.isArray(p.availability) && p.availability.includes(slot.id)
            );

            return (
              <div key={slot.id}>
                <div
                  className={`breakdown-item ${isBest ? "best" : ""} ${isExpanded ? "expanded" : ""}`}
                  onClick={() => setExpandedSlot(isExpanded ? null : slot.id)}
                  style={{ cursor: "pointer" }}
                >
                  <span className="breakdown-date">
                    {slot.dayName}, {slot.displayDate}
                  </span>
                  <span className="breakdown-count">
                    {count} votes {isExpanded ? "▼" : "▶"}
                  </span>
                </div>
                {isExpanded && (
                  <div className="breakdown-expanded">
                    {voters.length > 0 ? (
                      <ul className="voter-list">
                        {voters.map((voter) => (
                          <li key={voter.email} className="voter-item">
                            {voter.name}
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <div className="no-voters">No votes yet</div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

